"use client";
import { gsap } from "gsap";
import EventCard from "../EventCard";
import { useEffect, useRef } from "react";
import { Calendar } from "lucide-react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const YearlyEventsTimeline = ({ yearData, index, onActive }) => {
  const root = useRef(null);
  const { year, events = [] } = yearData;

  const colorThemes = [
    {
      text: "from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-500",
      iconBorder: "border-cyan-500/40",
      iconColor: "text-cyan-400",
      dot: "bg-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.7)]",
      badge: "bg-gradient-to-l from-cyan-500 to-purple-500",
    },

    {
      text: "from-emerald-600 to-blue-600 dark:from-emerald-400 dark:to-blue-500",
      iconBorder: "border-emerald-500/40",
      iconColor: "text-emerald-400",
      dot: "bg-emerald-400 shadow-[0_0_15px_rgba(52,211,153,0.7)]",
      badge: "bg-gradient-to-l from-emerald-500 to-blue-500",
    },

    {
      text: "from-orange-500 to-pink-600 dark:from-orange-400 dark:to-pink-500",
      iconBorder: "border-orange-500/40",
      iconColor: "text-orange-400",
      dot: "bg-orange-400 shadow-[0_0_15px_rgba(251,146,60,0.7)]",
      badge: "bg-gradient-to-r from-orange-500 to-pink-500",
    },
  ];
  const theme = colorThemes[index % colorThemes.length];

  useEffect(() => {
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: root.current,
        start: "top 45%",
        end: "bottom 45%",
        onToggle: (self) => self.isActive && onActive(year),
      });

      gsap.from(".event-card-item", {
        scrollTrigger: { trigger: root.current, start: "top 75%" },
        y: 50,
        opacity: 0,
        duration: 0.8,
        stagger: 0.2,
        ease: "power2.out",
      });
    }, root);

    return () => ctx.revert();
  }, [year, onActive]);

  if (!events.length) return null;

  return (
    <section ref={root} id={`year-${year}`} className="w-full  bg-background-primary py-5! md:py-10! !px-6 md:px-10 min-[2560px]:!py-24 min-[2560px]:!px-24">
      <div
        className="max-w-[4000px] mx-auto border border-border-primary rounded-[32px] min-[2560px]:rounded-[60px] !p-8 md:p-12 min-[2560px]:!p-28"
        style={{
          background: "var(--card-background)",
          boxShadow: "var(--card-shadow)",
        }}
      >
        <div className="flex flex-col lg:flex-row relative">
          <div className="lg:w-[250px] min-[2560px]:w-[450px] shrink-0 !mb-10 lg:mb-0">
            <div className="lg:sticky lg:top-10 min-[2560px]:top-24">
              <h2 className={`text-5xl min-[2560px]:text-8xl font-normal bg-clip-text text-transparent bg-gradient-to-r ${theme.text} !mb-1 min-[2560px]:!mb-4 tracking-tight`}>
                {year}
              </h2>
              <div className="text-2xl min-[2560px]:text-5xl font-medium text-text-primary mb-10! md:mb-20! min-[2560px]:!mb-40">Highlights</div>

              <div className="flex flex-col !gap-4 min-[2560px]:!gap-10">
                <div className="rounded-xl w-fit">
                  <Calendar className={`w-9 h-9 min-[2560px]:w-16 min-[2560px]:h-16 ${theme.iconColor}`} />
                </div>
                <div>
                  <div className="text-2xl min-[2560px]:text-5xl font-normal text-text-primary">
                    {events.length} {events.length === 1 ? "Event" : "Events"}
                  </div>
                  <div className="text-md min-[2560px]:text-2xl text-text-secondary mt-1!">this year</div>
                </div>
              </div>
            </div>
          </div>

          <div className=" hidden lg:block absolute  left-[245px] w-[1px] min-[2560px]:left-[262px]  min-[2560px]:w-[2px]  top-4 bottom-4  bg-border-primary "></div>

          <div className="flex-1 lg:!pl-16 min-[2560px]:lg:!pl-32 flex flex-col !gap-10 min-[2560px]:!gap-24">
            {events.map((event, i) => (
              <div key={event.id || i} className="event-card-item relative">
                <div
                  className={`hidden lg:block absolute top-8 min-[2560px]:top-16 -left-[67px] min-[2560px]:-left-[113px] -translate-x-1/2 w-4 h-4 min-[2560px]:w-8 min-[2560px]:h-8 rounded-full z-20 ${theme.dot} ring-4 ring-surface-primary min-[2560px]:ring-[8px]`}
                ></div>
                <EventCard event={event} index={i} theme={theme} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default YearlyEventsTimeline;
